import type { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";

import { shiftWindow, type ShiftTimes } from "~/shared/lib/shift-status";

/** `running`: the end is a floor, raised to the current time when the frame is drawn. */
export type ShiftSpan = { start?: Date; end?: Date; running?: boolean };

const at = (t: ConnectProto.DriverOnShift["startedAt"]) => fromTimestamp(t) ?? undefined;

const latest = (...dates: (Date | undefined)[]) =>
  dates.reduce<Date | undefined>((a, b) => (b && (!a || b > a) ? b : a), undefined);

/**
 * The shift's `shiftWindow`, except that a running one's end is a floor (its plan or a started
 * route's end) which `frameBounds` raises to now.
 */
export function shiftSpan(dos: ShiftTimes, ranUntil?: Date): ShiftSpan {
  const window = shiftWindow(dos);
  // Once a shift has started, shiftWindow leaves the end open only while it runs.
  if (at(dos.startedAt) && !window.end)
    return { start: window.start, end: latest(at(dos.twEndAt), ranUntil), running: true };
  return window;
}

/** The frame's [start, end] in ms; an edge the shift doesn't name comes from its routes. */
export function frameBounds(
  span: ShiftSpan | undefined,
  routes: { start: number; end: number },
  now: number,
): [number, number] {
  const start = span?.start?.getTime() ?? routes.start;
  // A running shift's own end is never its routes': unstarted ones carry placeholder plans.
  if (span?.running) return [start, Math.max(span.end?.getTime() ?? -Infinity, now)];
  return [start, span?.end?.getTime() ?? routes.end];
}

/** The first source naming a shift's times wins, so pass the fullest sources first. */
export function shiftSpansById(
  shifts: Iterable<ConnectProto.DriverOnShift | undefined>,
  ranUntilById: ReadonlyMap<string, Date> = new Map(),
): Map<string, ShiftSpan> {
  const spans = new Map<string, ShiftSpan>();
  for (const dos of shifts) {
    if (!dos?.id || spans.has(String(dos.id))) continue;
    const span = shiftSpan(dos, ranUntilById.get(String(dos.id)));
    if (span.start || span.end) spans.set(String(dos.id), span);
  }
  return spans;
}

/** Per shift, the latest end of a route that has actually started; unstarted routes are plans. */
export function ranUntilByShift(routes: Iterable<[string | undefined, ConnectProto.Route]>) {
  const out = new Map<string, Date>();
  for (const [dosId, r] of routes) {
    if (dosId === undefined || !at(r.factStartAt)) continue;
    const end = latest(at(r.factEndAt) ?? at(r.planEndAt), out.get(dosId));
    if (end) out.set(dosId, end);
  }
  return out;
}
